import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data";

const slug = (l) => l.toLowerCase().replace(/\s+/g, "-");

export default function Navbar({ onCta }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/10 py-4" : "bg-transparent py-6"
      }`}
      data-testid="navbar"
    >
      <div className="container-auvra flex items-center justify-between">
        <Link to="/" data-testid="logo" className="flex items-center group">
          <img
            src="/brand/auvra-logo-trim.png"
            alt="AUVRA"
            className="h-7 md:h-8 w-auto transition-transform group-hover:scale-105 duration-500"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV_LINKS.map((l) => (
            <Link
              key={l}
              to={`/${slug(l)}`}
              data-testid={`nav-${slug(l)}`}
              className="text-sm text-secondary-auvra hover:text-white transition-colors duration-300"
            >
              {l}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <Link to="/login" data-testid="sign-in-btn" className="text-sm text-white/80 hover:text-white transition-colors">
            Sign In
          </Link>
          <Link
            to="/signup"
            data-testid="nav-get-started-btn"
            className="text-sm font-medium bg-auvra-blue hover:bg-auvra-blue/90 text-white px-5 py-2.5 rounded-full transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]"
          >
            Get Started
          </Link>
        </div>

        <button
          className="lg:hidden text-white"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-black/95 border-t border-white/10 mt-4"
            data-testid="mobile-menu"
          >
            <div className="container-auvra py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <Link key={l} to={`/${slug(l)}`} onClick={() => setOpen(false)} className="text-secondary-auvra hover:text-white py-1">
                  {l}
                </Link>
              ))}
              <Link
                to="/signup"
                onClick={() => setOpen(false)}
                className="mt-2 bg-auvra-blue text-white py-3 rounded-full font-medium text-center"
                data-testid="mobile-get-started-btn"
              >
                Get Started
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
